import { DATABANK_SITE_URL } from "../constants/sites";

/**
 * Opening the Cost Databank with the member already signed in.
 *
 * The databank is a separate site on its own subdomain, so it cannot read the
 * token this portal keeps in localStorage — origins do not share storage. A
 * member pressing "Cost Databank" has signed in here a moment ago and should
 * not be asked again, so the token is handed over on the URL FRAGMENT, and the
 * databank keeps it for the life of the tab only (see that site's
 * lib/handoff.ts).
 *
 * ── Why the fragment, and why only on a click ───────────────────────────────
 * A fragment is never sent to a server, so the token reaches no access log and
 * no `Referer` — and the databank takes it off the address bar before it
 * renders.
 *
 * The URL is built HERE, when the link is pressed, and never written into an
 * `href`. A token sitting in the DOM is one that "copy link address" copies,
 * that a screenshot captures, and that goes stale while the page stays open.
 * The menu's own href stays the plain site address, so every other way of
 * following a link — middle click, open in new tab, copy — leads to the
 * databank's front page, where the member can sign in with their membership
 * number.
 *
 * ── What this is not ────────────────────────────────────────────────────────
 * It is the member's own portal token, so while the tab holds it, it can do
 * what the member can do. Closing the tab makes the databank forget it; it does
 * not revoke it. A genuinely short-lived, databank-only token would have to be
 * minted by the API.
 */

/** The fragment key the databank reads. Both sides name it. */
const HANDOFF_PARAM = "niqs_handoff";

/**
 * The databank's address, carrying this session if there is one to carry.
 *
 * Falls back to the plain address when the portal holds no token — the
 * databank then simply asks them to sign in, which is the right outcome and
 * not an error worth reporting.
 */
export function databankHandoffUrl(): string {
  let token: string | null = null;

  try {
    token = localStorage.getItem("token");
  } catch {
    token = null; // blocked storage: nothing to hand over
  }

  if (!token) return DATABANK_SITE_URL;

  return `${DATABANK_SITE_URL}/#${HANDOFF_PARAM}=${encodeURIComponent(token)}`;
}

/**
 * Open the databank in a new tab, signed in.
 *
 * A new tab rather than this one: the member keeps their place in the portal,
 * and the hand-off is scoped to a tab they opened on purpose. `noopener` so the
 * page cannot reach back through `window.opener` and navigate the portal.
 */
export function openDatabank(): void {
  window.open(databankHandoffUrl(), "_blank", "noopener,noreferrer");
}
